import React, {useEffect, useState, useMemo} from "react";
import { Box, Stack, Divider, Typography, Paper, LinearProgress, Grid } from "@mui/material";
import { BarChart } from '@mui/x-charts/BarChart';
import { School, FilterList } from "@mui/icons-material";

const ProgramsBarChart = ({ data }) => {
    const [maxItems, setMaxItems] = useState(8); // Number of programs to show
    
    // Process data
    const processedData = useMemo(() => {
        if (!data) return [];
        
        const totalCount = data.reduce((sum, item) => sum + (item.count || 0), 0);
        
        return data.map((item, index) => ({
            ...item,
            id: index,
            percentage: totalCount > 0 ? Math.round((item.count / totalCount) * 100) : 0,
            rank: index + 1
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, maxItems);
    }, [data, maxItems]);

    const totalCount = useMemo(() => {
        return data?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;
    }, [data]);

    const otherProgramsCount = useMemo(() => {
        if (!data || data.length <= maxItems) return 0;
        return data.slice(maxItems).reduce((sum, item) => sum + (item.count || 0), 0);
    }, [data, maxItems]);

    const otherProgramsPercentage = useMemo(() => {
        return totalCount > 0 ? Math.round((otherProgramsCount / totalCount) * 100) : 0;
    }, [otherProgramsCount, totalCount]);

    return (
        <Box sx={{ p: 2, height: '100%' }}>
            <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <Typography variant="h6" fontWeight="bold">
                    Program distribution
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    Program distribution ratio
                </Typography>
                <Divider sx={{ mt: 1 }} />
            </Box>

            {/* Horizontal Bars */}
            <Box sx={{ height: 230, overflow: 'auto', pr: 1 }}>
                <Stack spacing={2}>
                    {processedData.map((program, index) => (
                        <Box key={program.id}>
                            <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 150 }}>
                                    {index + 1}. {program.program_name}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" fontWeight="bold">
                                        {program.count}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        ({program.percentage}%)
                                    </Typography>
                                </Stack>
                            </Stack>
                            <LinearProgress 
                                variant="determinate" 
                                value={program.percentage}
                                sx={{ 
                                    height: 12,
                                    borderRadius: 6,
                                    bgcolor: '#e0e0e0',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 6,
                                        bgcolor: index === 0 ? '#1976d2' : 
                                                 index === 1 ? '#2e7d32' : 
                                                 index === 2 ? '#ff9800' : 
                                                 index === 3 ? '#d32f2f' : 
                                                 index === 4 ? '#9c27b0' : 
                                                 index === 5 ? '#0097a7' : 
                                                 index === 6 ? '#ff5722' : '#795548'
                                    }
                                }}
                            />
                        </Box>
                    ))}
                    
                    {/* "Other Programs" Bar */}
                    {otherProgramsCount > 0 && (
                        <Box>
                            <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 150 }}>
                                    Other {data.length - maxItems} programs
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {otherProgramsCount}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        ({otherProgramsPercentage}%)
                                    </Typography>
                                </Stack>
                            </Stack>
                            <LinearProgress 
                                variant="determinate" 
                                value={otherProgramsPercentage}
                                sx={{ 
                                    height: 12,
                                    borderRadius: 6,
                                    bgcolor: '#e0e0e0',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 6,
                                        bgcolor: '#9e9e9e'
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Stack>
            </Box>

            {/* Summary Stats */}
            <Paper sx={{ p: 1.5, mt: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <Box textAlign="center">
                            <Typography variant="h6" fontWeight="bold">
                                {data?.length || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Total Programs
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={4}>
                        <Box textAlign="center">
                            <Typography variant="h6" fontWeight="bold" color="info">
                                {totalCount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Total Sessions
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={4}>
                        <Box textAlign="center">
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                                {processedData[0]?.percentage || 0}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Top Program
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
}

export default ProgramsBarChart;